import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { fetchServices } from '../../api/serviceApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater';
import NoContentMessage from '../../components/NoContentMessage';

function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const servicesPerPage = 12;

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        setLoading(true);
        const data = await fetchServices({ paginate: true, per_page: servicesPerPage, page: currentPage });

        if (data.success) {
          const fetchedServicesData = data.data;
          const paginationMeta = data.pagination;

          const formattedServices = fetchedServicesData.map(service => ({
            id: service.id,
            title: service.name,
            description: service.excerpt,
            slug: service.slug,
            image: service.image, // Include the image URL
          }));
          setServices(formattedServices);
          setTotalPages(paginationMeta.last_page || 1);
        } else {
          addToast('error', data.message || 'Failed to fetch services.');
          setServices([]);
          setTotalPages(1);
        }
      } catch (e) {
        console.error("Error fetching services:", e);
        addToast('error', 'Failed to load services. Please try again later.');
        setServices([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchServicesData();
  }, [addToast, location.search, currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      const newQueryParams = new URLSearchParams(location.search);
      newQueryParams.set('page', pageNumber);
      navigate(`?${newQueryParams.toString()}`);
    }
  };

  return (
    <div className="pt-20 bg-bg-dark min-h-screen text-text-gray">
      <MetaTagsUpdater pageKey="Services" />
      <div className="max-w-6xl mx-auto px-5 py-8">
        <h1 className="text-4xl font-bold text-center text-text-white mb-16 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
          My Services
        </h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: servicesPerPage }).map((_, index) => (
              <div key={index} className="bg-bg-light p-8 rounded-lg shadow-md border border-border-subtle animate-pulse">
                <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-700 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6 mb-4"></div>
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-1"></div>
                <div className="h-4 bg-gray-700 rounded w-2/3"></div>
                <div className="h-4 bg-gray-700 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : services.length > 0 ? (
          <>
            <section className="mb-16">
              <p className="text-lg text-center text-text-gray max-w-3xl mx-auto mb-12">
                I offer a comprehensive range of services designed to help businesses and individuals
                achieve their digital goals. From conceptualization to deployment and maintenance,
                I provide end-to-end solutions tailored to your specific needs.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {services.map(service => (
                  <div key={service.id} className="bg-bg-light p-0 rounded-lg shadow-md border border-border-subtle hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    {service.image && (
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-48 object-cover"
                      />
                    )}
                    <div className="p-8">
                      <h3 className="text-2xl font-semibold text-accent-red mb-4">{service.title}</h3>
                      <p className="text-text-gray mb-4">{service.description}</p>
                      <Link to={`/services/${service.slug}`} className="text-accent-red hover:underline">
                        Learn More →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-4 py-2 rounded-md ${
                      currentPage === i + 1 ? 'bg-accent-red text-white' : 'bg-gray-700 text-text-white hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <NoContentMessage message="No services found." /> // Use common component
        )}
      </div>
    </div>
  );
}

export default Services;